import mixpanel from "mixpanel-browser";
import { https } from "../http";
import store from "../store";
import {
  _login,
  setEmailVerifyLoading,
  setLoading,
  setForgotLoading,
  setResetLoading,
  setContactUs,
} from "../store/reducers/auth";
import {
  clearSession,
  removeCookie,
  setCookie,
  showError,
  showInfo,
  showSuccess,
} from "../utils/index";
import { getProfile } from "./profile";
import swal from "sweetalert";
import { userInfoAnalytics } from "../config/mixpanelConfig";
import { getAllStaff } from "./staff";

// Format seconds into "2 minutes 5 seconds" style
const formatRetryAfter = (seconds) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const minPart = mins > 0 ? `${mins} ${mins === 1 ? 'minute' : 'minutes'}` : '';
  const secPart = secs > 0 ? `${secs} ${secs === 1 ? 'second' : 'seconds'}` : '';
  return [minPart, secPart].filter(Boolean).join(' ');
};

// Build a clean, professional message for 429 responses
const getRateLimitMessage = (error) => {
  const data = error?.response?.data;
  const status = error?.response?.status;
  if (status === 429) {
    if (data?.limitType === 'daily') {
      return 'You have reached the daily submission limit for this form. Please try again tomorrow.';
    }
    if (data?.retryAfter) {
      return `Please wait ${formatRetryAfter(data.retryAfter)} before submitting again.`;
    }
    return 'Too many submissions. Please try again later.';
  }
  return data?.error || data?.message || 'Something went wrong';
};

const login = async (values, navigate, { onLocationStatus } = {}) => {
  try {
    // Submitting credentials always starts a fresh session — drop any
    // previous user's cookie/store BEFORE authenticating so a failed or
    // abandoned login can't fall back into the old account.
    clearSession();
    store.dispatch(setLoading(true));
    let response;
    try {
      response = await https().post("/auth/login", values);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 403 && data?.loginLocationRequired) {
        if (data?.loginLocationDenied) {
          store.dispatch(setLoading(false));
          onLocationStatus?.({ status: 'denied', message: data.message, maxDistance: data.maxDistance, currentDistance: data.currentDistance });
          return;
        }
        onLocationStatus?.({ status: 'acquiring', message: 'Verifying your location...' });
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000 })
          );
          const loginLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          onLocationStatus?.({ status: 'verifying', message: 'Checking distance to facility...' });
          response = await https().post("/auth/login", { ...values, loginLocation });
          onLocationStatus?.({ status: 'success', message: 'Location verified!' });
        } catch (locErr) {
          store.dispatch(setLoading(false));
          if (locErr?.response?.data?.loginLocationDenied) {
            onLocationStatus?.({ status: 'denied', message: locErr.response.data.message, maxDistance: locErr.response.data.maxDistance, currentDistance: locErr.response.data.currentDistance });
          } else {
            onLocationStatus?.({ status: 'error', message: 'Unable to get your location. Please enable location services in your browser settings and try again.' });
          }
          return;
        }
      } else {
        throw err;
      }
    }
    const { message, token, user } = response?.data;

    if (response?.status === 200) {
      if (message === "Email Verified") {
        //Check if token exists (From backend token will be sent only when 2FA is not enabled)
        if (token) {
          clearSession();
          store.dispatch(_login({ token }));
          let profile = await getProfile(true, token);
          // If token is true then check user type
          if (profile.user?.user?.type !== "staff") {
            if (user?.is2FaAccess === true && token) {
              navigate("/select-2fa-method", {
                state: { user: user },
              });
              return; // ← Exits here if 2FA is enabled
            }

            // If user type is admin then set the token and move forward
            setCookie(token, "token");

            // free account redirect to select price page if user is admin
            // if (profile?.isFreeAccount) {
            //   navigate("/selectprice", {
            //     state: { isFreeAccount: true },
            //   });
            //   return;
            // }

            if (!profile?.user?.business) {
              navigate("/signup", {
                state: {
                  token: token,
                  businessName: response?.data?.user?.businessName,
                },
              });
              // navigate("/selectprice");
            } else if (profile.user.business && !profile.user.subscription) {
              navigate("/selectprice");
            } 
            // else if (profile?.isFreeAccount) {
            //   navigate("/selectprice", {
            //     state: { isFreeAccount: true },
            //   });
            // } 
            else {
              navigate("/home");
            }
          } else if (profile?.user?.user?.type === "staff") {
            // if (profile?.isFreeAccount) {
            //   navigate("/free-plan-users");
            //   return;
            // }
            if (user?.user?.is2FaAccess === true && token) {
              navigate("/select-2fa-method", {
                state: { user: user?.user },
              });
              return;
            }
            // If user type is staff then check if staff has multiple businesses
            if (response.data?.user?.user?.businesses?.length > 1) {
              // If staff has multiple businesses then navigate to select businesses page
              navigate("/select-busniesses", {
                state: { res: response.data.user.user },
              });
            } else {
              // If staff has single business then set the token and move to dashboard
              setCookie(token, "token");
              navigate("/home");
            }
          }
        } else {
          // If token is not present then check the user type
          if (response?.data.user?.user?.type !== "staff") {
            // If user type is admin then navigate to 2fa screen
            navigate("/ ", {
              state: { user: response.data.user },
            });
          } else {
            // If user type is staff then check if staff has multiple businesses
            if (response.data?.user?.user?.businesses?.length > 1) {
              // If staff has multiple businesses then navigate to select businesses page
              navigate("/select-busniesses", {
                state: { res: response.data.user.user },
              });
            } else {
              // If staff has single business then navigate to 2fa screen
              navigate("/select-2fa-method", {
                state: { user: response?.data?.user?.user },
              });
            }
          }
          // store.dispatch(_login({ token }));
        }
      } else if (message === "Verification Email Sent") {
        showInfo("Verification Email Sent");
      }
      store.dispatch(setLoading(false));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status, false);
  }
};

const send2faCode = async (values, navigate) => {
  try {
    let response = await https().post("/auth/send-2fa", values);
    if (response?.status === 200) {
      showInfo(response?.data?.message || "");
      navigate("/2fa", {
        state: { user: response.data.user },
      });
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const verify2fa = async (values, navigate) => {
  try {
    let response = await https().post("/auth/verify-2fa", values);
    if (response?.status === 200) {
      let { token } = response?.data;
      if (token) {
        clearSession();
        store.dispatch(_login({ token }));
        let profile = await getProfile(true, token);
        userInfoAnalytics(profile);
        if (profile.user?.user?.type !== "staff") {
          setCookie(token, "token");
          if (!profile?.user?.business) {
            navigate("/signup");
            // navigate("/selectprice");
          } else if (profile.user.business && !profile.user.subscription) {
            navigate("/selectprice");
          } else {
            navigate("/home");
          }
        } else {
          navigate("/home");
          setCookie(token, "token");
        }
      }
      else {
        navigate("/home");
      }
      return response?.data;
    }
  } catch (error) {
    const resData = error?.response?.data;
    showError(getRateLimitMessage(error));
    if (
      resData?.user &&
      resData?.user?.lockUntil &&
      new Date(resData.user.lockUntil) > new Date()
    ) {
      navigate("/login", { replace: true });
    }
  }
};

const resetStaff2FA = async (values, navigate) => {
  try {
    let response = await https().post("/auth/reset-staff-2fa", values);
    if (response?.status === 200) {
      getAllStaff("all");
      return response?.data;
    }
  } catch (error) {
    const resData = error?.response?.data;
    showError(resData?.error || resData?.message || "Something went wrong");
  }
};
const staffAndProfile2fa = async (values, navigate) => {
  try {
    let response = await https().post("/auth/staff-profile-verify-2fa", values);
    if (response?.status === 200) {
      getAllStaff("all");
      return response?.data;
    }
  } catch (error) {
    const resData = error?.response?.data;
    showError(resData?.error || resData?.message || "Something went wrong");
  }
};

const resend2fa = async (values) => {
  try {
    let response = await https().post("/auth/resend-code", values);
    if (response?.status === 200) {
      showSuccess(response?.data?.message);
      return response?.data;
    }
  } catch (error) {
    showError(
      error?.response?.data?.error || error?.response?.data?.message,
      error?.response?.status
    );
  }
};

const verifyTwoFactor = async (values) => {
  try {
    let response = await https().post("/auth/verify-two-factor", values);
    if (response?.status === 200) {
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};

const twoFactorSettings = async (values) => {
  try {
    let response = await https().post("/auth/two-factor-settings", values);
    if (response?.status === 200) {
      getProfile();
      return response;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};
/**
 * Change the signed-in user's own password.
 *
 * Unlike most services here this one does NOT pop a swal on failure — the
 * Security settings form renders the error inline against the right field.
 * Resolves to `{ success: true, message }` or `{ success: false, message }`.
 *
 * The change invalidates every token issued before it, so the response carries a
 * replacement token for this device. Storing it is what keeps the current
 * session alive while every other device gets signed out — skip it and the very
 * next request from this tab 401s.
 */
const changePassword = async ({ currentPassword, newPassword }) => {
  try {
    let response = await https().post("/auth/change-password", {
      currentPassword,
      newPassword,
    });
    const freshToken = response?.data?.token;
    if (freshToken) {
      setCookie(freshToken, "token");
    }
    return {
      success: true,
      message: response?.data?.message || "Password updated successfully",
    };
  } catch (error) {
    const resData = error?.response?.data;
    return {
      success: false,
      message:
        resData?.message ||
        resData?.error ||
        "Unable to update your password. Please try again.",
    };
  }
};

/**
 * Devices currently signed in as this user. The caller's own session comes back
 * flagged `current: true`.
 *
 * Like changePassword above, these three resolve to a result object instead of
 * popping a swal, so the Security panel can render state inline.
 */
const getSessions = async () => {
  try {
    let response = await https().get("/auth/sessions");
    return { success: true, sessions: response?.data?.sessions || [] };
  } catch (error) {
    return {
      success: false,
      sessions: [],
      message:
        error?.response?.data?.message || "Unable to load your active devices.",
    };
  }
};

/** Sign out a single device. The backend refuses to revoke the current one. */
const revokeSession = async (sessionId) => {
  try {
    let response = await https().delete(`/auth/sessions/${sessionId}`);
    return { success: true, message: response?.data?.message };
  } catch (error) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Unable to sign out that device.",
    };
  }
};

/** Sign out every device except this one. */
const revokeOtherSessions = async () => {
  try {
    let response = await https().delete("/auth/sessions/others");
    return {
      success: true,
      message: response?.data?.message,
      revoked: response?.data?.revoked || 0,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Unable to sign out your other devices.",
    };
  }
};

const selectedBusiness = async (values) => {
  try {
    let response = await https().post("/auth/selected-business", values);
    if (response?.status === 200) {
      // getProfile();
      return response;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};
const getUserByEmail = async (values) => {
  try {
    let response = await https().post("/auth/get-user-by-email", values);
    if (response?.status === 200) {
      // getProfile();
      return response;
    }
  } catch (error) {
    showError(error?.response?.data?.message);
  }
};

// google login
const googleLogin = async (values, navigate) => {
  try {
    store.dispatch(setLoading(true));
    let response = await https().post("/auth/google-auth", values);
    if (response?.status === 200) {
      let { token, user } = response?.data;
      clearSession();
      store.dispatch(_login({ token }));
      let profile = await getProfile(true, token);
      if (profile) {
        // console.log("user :", user);
        setCookie(token, "token");
        if (profile.user?.user?.type !== "staff") {
          // console.log("user", user);
          if (!profile?.user?.business) {
            navigate("/signup");
            // navigate("/selectprice");
          } else if (profile.user.business && !profile.user.subscription) {
            navigate("/selectprice");
          } else {
            navigate("/home");
          }
        } else {
          navigate("/home");
        }
      }
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status, false);
  }
};

// google sign up
const googleSignup = async (values, navigate) => {
  try {
    store.dispatch(setLoading(true));
    let response = await https().post("/auth/decode-token", values);
    if (response?.status === 200) {
      clearSession();
      setCookie(response?.data?.user?.token, "token");
      store.dispatch(setLoading(false));
      navigate("/profile");
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status, false);
  }
};

const signUp = async (values) => {
  try {
    store.dispatch(setLoading(true));
    let response = await https().post("/auth/sign-up", values);
    if (response?.status === 200) {
      clearSession();
      setCookie(response?.data?.user?.token, "token");
      store.dispatch(setLoading(false));
      // navigate("/profile");
      await swal({
        title: `Email Verification`,
        text: "Email verification link sent successfully. Please verify your email address",
        icon: "success",
        dangerMode: false,
        buttons: ["Cancel", "Ok"],
      });
      return response;
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(getRateLimitMessage(error), error?.response?.status);
    return error?.response;
  }
};

const registerBusiness = async (values, navigate) => {
  try {
    store.dispatch(setLoading(true));
    let response = await https().post("/auth/business", values);

    if (response?.status === 200) {
      await getProfile(true, response?.data?.token);
      store.dispatch(setLoading(false));
      return response;
      // navigate("/selectprice");
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    const duplicate = error?.response?.data?.message
      ?.toLowerCase()
      ?.includes("duplicate");
    const companyName = error?.response?.data?.message
      ?.toLowerCase()
      ?.includes("companyname");
    // console.log("erroe in sercice", error?.response?.data?.message);
    const businessEmail = error?.response?.data?.message
      ?.toLowerCase()
      ?.includes("businessemail");
    const contactPhone = error?.response?.data?.message
      ?.toLowerCase()
      ?.includes("contactphone");

    if (duplicate && companyName)
      return showError("Company name alreay in use.");
    if (duplicate && businessEmail)
      return showError("Company Email alreay in use.");
    if (duplicate && contactPhone)
      return showError("Company Number alreay in use.");
    else showError(error?.response?.data?.message, error?.response?.status);
  }
};

const forgetPassword = async (values, navigate) => {
  try {
    store.dispatch(setForgotLoading(true));
    let response = await https().post("/auth/forgotPassword", values);

    if (response?.status === 200) {
      store.dispatch(setForgotLoading(false));
      showInfo(response?.data?.message);
    }
  } catch (error) {
    store.dispatch(setForgotLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const resetPassword = async (values, navigate) => {
  try {
    store.dispatch(setResetLoading(true));
    let response = await https().post("/auth/resetPassword", values);
    if (response?.status === 200) {
      showInfo(response?.data?.message);
      removeCookie("token");
      navigate("/login");
    }
  } catch (error) {
    store.dispatch(setResetLoading(false));

    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const verifyEmail = async (values, navigate) => {
  try {
    store.dispatch(setEmailVerifyLoading(true));
    let response = await https().post("/auth/verifyEmail", values);

    // console.log("response inside verify email");

    if (response?.status === 200) {
      store.dispatch(setEmailVerifyLoading(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setEmailVerifyLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};
const contactUs = async (values) => {
  try {
    store.dispatch(setContactUs(true));
    let response = await https().post("/auth/contactUs", values);

    if (response?.status === 200) {
      store.dispatch(setContactUs(false));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setContactUs(false));
    showError(getRateLimitMessage(error), error?.response?.status);
    throw error;
  }
};

const inviteContractor = async (values) => {
  try {
    let response = await https().post("/skypointContractor/invite", values);

    if (response?.status === 200) {
      showInfo(response?.data?.message);
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const searchContractorByEmail = async (email) => {
  try {
    let response = await https().get(`/skypointContractor/search-contractor?email=${encodeURIComponent(email)}`);
    if (response?.status === 200) {
      return response?.data?.user;
    }
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

const requestContractorAccess = async (contractorId) => {
  try {
    let response = await https().post("/skypointContractor/request-contractor", { contractorId });
    if (response?.status === 200) {
      showInfo(response?.data?.message);
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

const removeContractor = async (contractorId) => {
  try {
    let response = await https().put("/skypointContractor/remove-contractor", { contractorId });
    if (response?.status === 200) {
      showInfo(response?.data?.message);
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const setupTotp = async (values) => {
  try {
    let response = await https().post("/auth/totp/setup", values);
    if (response?.status === 200) {
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.error || error?.response?.data?.message);
  }
};

const verifyTotpSetup = async (values) => {
  try {
    let response = await https().post("/auth/totp/verify-setup", values);
    if (response?.status === 200) {
      getProfile();
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.error || error?.response?.data?.message);
  }
};

const disableTotp = async (values) => {
  try {
    let response = await https().post("/auth/totp/disable", values);
    if (response?.status === 200) {
      getProfile();
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.error || error?.response?.data?.message);
  }
};

const verifyTotpLogin = async (values, navigate) => {
  try {
    let response = await https().post("/auth/verify-totp-login", values);
    if (response?.status === 200) {
      let { token } = response?.data;
      if (token) {
        clearSession();
        store.dispatch(_login({ token }));
        let profile = await getProfile(true, token);
        userInfoAnalytics(profile);
        if (profile.user?.user?.type !== "staff") {
          setCookie(token, "token");
          if (!profile?.user?.business) {
            navigate("/signup");
          } else if (profile.user.business && !profile.user.subscription) {
            navigate("/selectprice");
          } else {
            navigate("/home");
          }
        } else {
          navigate("/home");
          setCookie(token, "token");
        }
      } else {
        navigate("/home");
      }
      return response?.data;
    }
  } catch (error) {
    const resData = error?.response?.data;
    showError(getRateLimitMessage(error));
    if (
      resData?.user &&
      resData?.user?.lockUntil &&
      new Date(resData.user.lockUntil) > new Date()
    ) {
      navigate("/login", { replace: true });
    }
  }
};

export {
  login,
  googleLogin,
  googleSignup,
  signUp,
  forgetPassword,
  verifyEmail,
  resetPassword,
  registerBusiness,
  contactUs,
  setResetLoading,
  verify2fa,
  verifyTwoFactor,
  send2faCode,
  resend2fa,
  twoFactorSettings,
  changePassword,
  getSessions,
  revokeSession,
  revokeOtherSessions,
  staffAndProfile2fa,
  resetStaff2FA,
  selectedBusiness,
  getUserByEmail,
  inviteContractor,
  searchContractorByEmail,
  requestContractorAccess,
  removeContractor,
  setupTotp,
  verifyTotpSetup,
  disableTotp,
  verifyTotpLogin,
};
